/** /notifications/find-replace
 *
 * Cross-model content search + audited batch replacement.
 *
 * A row here is one FIELD (`ndm:40013:email_message`), not one record — so row
 * ids are composite STRINGS. That is why this is a raw `DataGridPro` rather than
 * `ResourceDataGrid`: the latter coerces selection ids with `Number()` and would
 * silently drop every row (the delivery-logs grid uses a raw grid for the same
 * reason, with UUIDs).
 *
 * Two things this page must never do quietly, because both turn a missed match
 * into a confident "0 results":
 *   - hide that the result set was capped, or that kinds were skipped for lack
 *     of permission;
 *   - hide that a regex was narrowed by an auto-derived literal.
 * All three are surfaced above the grid.
 */
import FindReplaceIcon from '@mui/icons-material/FindReplace';
import { Alert, AlertTitle, Box, Chip, Typography } from '@mui/material';
import {
  DataGridPro,
  gridColumnFieldsSelector,
  useGridApiRef,
  type GridPaginationModel,
  type GridRowSelectionModel,
  type GridSlots,
  type GridToolbarProps,
} from '@mui/x-data-grid-pro';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { ConsoleApiError, isStatementTimeout } from '@/api/client';
import * as api from '@/api/contentSearch';
import type { ContentSearchRow } from '@/api/contentSearch';
import { orderColumns } from '@/components/DataGrid/columnOrder';
import { type BatchActionDef } from '@/components/DataGrid/batchTypes';
import { GridControlsToolbar } from '@/components/DataGrid/GridControlsToolbar';
import { gridRefreshToolbar } from '@/components/DataGrid/useGridAutoRefresh';
import { SearchTimeoutAlert } from '@/components/DataGrid/SearchTimeoutAlert';
import { PageHeader } from '@/components/PageHeader';
import {
  QuerySearchInput,
  parse,
  serialize,
  toWireAst,
  type FilterNode,
} from '@/components/QuerySearch';
import { useSnackbar } from '@/components/Snackbar';
import { useGridPresets, type PresetFiltersAdapter } from '@/hooks/useGridPresets';
import { formatRelativeTime } from '@/utils/format';
import { PAGE_FILL_SX } from '@/layout/pageFill';

import { buildContentColumns } from './contentColumns';
import { buildContentNamespaces } from './contentNamespaces';
import { ReplaceDialog } from './ReplaceDialog';

const DEFAULT_PAGE_SIZE = 50;

const PRESET_FILTERS: PresetFiltersAdapter = {
  urlParams: ['q'],
  capture: (get) => {
    const q = get('q');
    return q ? { q } : undefined;
  },
  toUrlParams: (filters) => {
    const q = (filters as { q?: unknown } | null)?.q;
    return typeof q === 'string' && q ? { q } : ({} as Record<string, string>);
  },
};

export function FindReplacePage() {
  const snackbar = useSnackbar();
  const [sp, setSp] = useSearchParams();
  const apiRef = useGridApiRef();

  const slotsQuery = useQuery({
    queryKey: ['content-search', 'slots'],
    queryFn: () => api.slots(),
    staleTime: 5 * 60_000,
  });
  const namespaces = useMemo(
    () => buildContentNamespaces(slotsQuery.data?.scopes.map((s) => s.value)),
    [slotsQuery.data],
  );

  const presets = useGridPresets({
    grid: 'content_search',
    defaultOrder: useMemo(() => buildContentColumns().map((c) => c.field), []),
    defaultHidden: [],
    defaultOrdering: 'kind',
    filters: PRESET_FILTERS,
  });

  const q = presets.getParam('q') ?? '';
  const ordering = presets.ordering;

  const { tree, diagnostics } = useMemo(() => {
    const r = parse(q, namespaces);
    return { tree: r.nodes, diagnostics: r.diagnostics };
  }, [q, namespaces]);

  const lastDiagKey = useRef('');
  useEffect(() => {
    if (diagnostics.length === 0) return;
    const key = `${q}::${diagnostics.length}`;
    if (key === lastDiagKey.current) return;
    lastDiagKey.current = key;
    snackbar.show(
      `Some of your search couldn't be interpreted: ${diagnostics[0]!.message}`,
      'warning',
    );
  }, [diagnostics, q, snackbar]);

  const [pagination, setPagination] = useState<GridPaginationModel>({
    page: 0,
    pageSize: DEFAULT_PAGE_SIZE,
  });
  const [extendedTimeout, setExtendedTimeout] = useState(false);

  /** Bumped to force a re-run. It is part of the react-query key AND drives the
   *  `refresh=1` param, because the result set is pinned SERVER-side: refetching
   *  without it would re-render the identical rows and imply they are current. */
  const [refreshNonce, setRefreshNonce] = useState(0);
  const pendingRefresh = useRef(false);
  const forceRefresh = useCallback(() => {
    pendingRefresh.current = true;
    setRefreshNonce((n) => n + 1);
  }, []);

  useEffect(() => {
    setPagination((p) => ({ ...p, page: 0 }));
  }, [q]);

  const { beforeUrlWrite } = presets;
  const setQuery = useCallback(
    (nodes: FilterNode[]) => {
      const next = new URLSearchParams(sp);
      beforeUrlWrite(next);
      const str = serialize(nodes);
      if (str) next.set('q', str);
      else next.delete('q');
      setSp(next, { replace: true });
    },
    [sp, setSp, beforeUrlWrite],
  );

  const hasSearch = tree.length > 0;

  const query = useQuery({
    queryKey: [
      'content-search',
      q,
      ordering,
      pagination.page,
      pagination.pageSize,
      extendedTimeout,
      refreshNonce,
    ],
    queryFn: async () => {
      // Only the request that the refresh triggered carries `refresh=1`; paging
      // through the freshly pinned set afterwards must NOT keep recomputing it.
      const refresh = pendingRefresh.current;
      pendingRefresh.current = false;
      return api.search({
        filters: toWireAst(tree),
        ordering,
        page: pagination.page + 1,
        page_size: pagination.pageSize,
        ...(extendedTimeout ? { timeout: 'extended' as const } : {}),
        ...(refresh ? { refresh: true } : {}),
      });
    },
    placeholderData: keepPreviousData,
    enabled: hasSearch && !presets.blocking,
  });

  const rows = useMemo(() => query.data?.results ?? [], [query.data?.results]);
  const rowCount = query.data?.count ?? 0;
  const searchKey = query.data?.search_key ?? '';

  // ---- selection (composite string ids, carried across pages) --------------
  const [selectionModel, setSelectionModel] = useState<GridRowSelectionModel>([]);
  const [allMatching, setAllMatching] = useState(false);

  const queryIdentity = useMemo(() => JSON.stringify({ q, ordering }), [q, ordering]);
  const lastIdentityRef = useRef(queryIdentity);
  useEffect(() => {
    if (lastIdentityRef.current !== queryIdentity) {
      lastIdentityRef.current = queryIdentity;
      setSelectionModel([]);
      setAllMatching(false);
      setExtendedTimeout(false);
    }
  }, [queryIdentity]);

  const selectedIds = useMemo(() => selectionModel.map(String), [selectionModel]);
  const selectedCount = allMatching ? rowCount : selectedIds.length;

  const clearSelection = useCallback(() => {
    setSelectionModel([]);
    setAllMatching(false);
  }, []);

  const handleSelectionChange = useCallback((model: GridRowSelectionModel) => {
    setSelectionModel(model);
    setAllMatching((prev) => (prev ? false : prev));
  }, []);

  /** Identity fields in the explicit selection. For `all_matching` the backend
   *  reports them per row in the dry run, so we only count what we can see. */
  const readOnlyCount = useMemo(() => {
    if (allMatching) return 0;
    const chosen = new Set(selectedIds);
    return rows.filter((r) => chosen.has(r.id) && !r.replaceable).length;
  }, [allMatching, selectedIds, rows]);

  // Replace goes through the standard batch-actions menu like every other
  // console grid. It uses the `dialog` extension point (level 1 of
  // BatchActionDef) rather than the generic `/batch/` dispatch, because this
  // resource has no `ConsoleBatchMixin` endpoint — its selection is
  // (kind, pk, field) triples against a pinned result set, not `pk__in` over one
  // model. The menu, permission gating, selection banner and "select all
  // matching" are all still the shared ones.
  const batchActions = useMemo<BatchActionDef[]>(
    () => [
      {
        key: 'replace',
        label: 'Replace…',
        permission: 'console_content_replace',
        icon: <FindReplaceIcon fontSize="small" />,
        // Everything captured here is frozen at click time, because the menu
        // snapshots this element. That is the right semantics: the selection
        // cannot change behind a modal, and `search_key` is derived from the
        // query, so a re-run re-pins the SAME key rather than issuing a new one.
        dialog: (ctx) => (
          <ReplaceDialog
            open
            onClose={ctx.close}
            searchKey={searchKey}
            selection={
              ctx.selection.mode === 'all_matching'
                ? { mode: 'all_matching' }
                : { mode: 'rows', rows: ctx.selection.ids.map(String) }
            }
            selectedCount={ctx.count}
            readOnlyCount={readOnlyCount}
            onDispatched={() => ctx.clearSelection()}
            onRerunSearch={forceRefresh}
          />
        ),
      },
    ],
    [searchKey, readOnlyCount, forceRefresh],
  );

  const columns = useMemo(
    () => orderColumns(buildContentColumns(), presets.orderedFields),
    [presets.orderedFields],
  );

  const apiError =
    query.error instanceof ConsoleApiError &&
    query.error.status === 400 &&
    !isStatementTimeout(query.error)
      ? query.error
      : null;
  const backendMessage =
    (apiError?.body as { detail?: string } | undefined)?.detail ?? apiError?.message ?? null;

  const data = query.data;
  const prefilter = data?.prefilter_used ?? [];
  const derivedPrefilter =
    prefilter.length > 0 &&
    !tree.some((n) => n.kind === 'cond' && n.namespace === 'text') &&
    tree.some((n) => n.kind === 'cond' && n.namespace === 're');

  return (
    <Box sx={PAGE_FILL_SX}>
      <PageHeader
        helpStory="find-replace"
        title="Find & Replace"
        subtitle="Search every source of notification content, then change it in bulk — with a dry run, an audit trail and a reason."
      />

      <Box sx={{ mb: 2, minWidth: 0 }}>
        <QuerySearchInput
          namespaces={namespaces}
          value={tree}
          onChange={setQuery}
          placeholder={'Search content…  e.g. in=messages & text=~Versailes'}
        />
      </Box>

      {data?.cached && data.computed_at && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
          Showing pinned results from {formatRelativeTime(data.computed_at)}. Refresh to re-run.
        </Typography>
      )}

      {!hasSearch && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <AlertTitle>Searches text, not references</AlertTitle>
          This finds text wherever it is stored — message bodies, blocks, themes, language-policy
          rules, rules, tag attributes, notification strings and standard templates. It does{' '}
          <strong>not</strong> follow references: a notification that uses a tag through its Tags
          list, or a ruleset through <code>{'{% if-ruleset 123 %}'}</code>, will not appear unless
          the text you searched for is literally in the content. For reference questions use the{' '}
          <strong>Notifications</strong> grid, which has <code>block=</code>, <code>list=</code>,{' '}
          <code>theme=</code> and <code>language_policy=</code>.
        </Alert>
      )}

      {isStatementTimeout(query.error) && (
        <SearchTimeoutAlert
          extendedAttempted={extendedTimeout}
          onRetryExtended={!extendedTimeout ? () => setExtendedTimeout(true) : undefined}
          retrying={query.isFetching}
        />
      )}
      {backendMessage && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {backendMessage}
        </Alert>
      )}

      {data?.capped && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <AlertTitle>Results are incomplete</AlertTitle>
          {data.capped_kinds.join(', ')} hit the per-type result cap, so some matches are not shown.
          Narrow the search (add a tenant, or a more specific fragment) before replacing anything.
        </Alert>
      )}

      {data && data.skipped_kinds.length > 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Not searched — you lack permission:{' '}
          {data.skipped_kinds.map((k) => k.label).join(', ')}.
        </Alert>
      )}

      {derivedPrefilter && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Your pattern was narrowed using the literal{' '}
          {prefilter.map((p) => (
            <Chip key={p} size="small" label={p} sx={{ mx: 0.5, fontFamily: 'monospace' }} />
          ))}
          before the full expression was applied. Matches that do not contain it are not found.
        </Alert>
      )}

      {data && (data.skipped_large.length > 0 || data.timed_out.length > 0) && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {data.skipped_large.length > 0 &&
            `${data.skipped_large.length} field(s) were too large to scan. `}
          {data.timed_out.length > 0 &&
            `${data.timed_out.length} field(s) hit the pattern timeout — simplify the expression.`}
        </Alert>
      )}

      <DataGridPro<ContentSearchRow>
        apiRef={apiRef}
        rows={rows}
        rowCount={rowCount}
        getRowId={(r) => r.id}
        columns={columns}
        loading={query.isLoading || query.isFetching}
        sx={{ flex: 1, minHeight: 360 }}
        getRowHeight={() => 'auto'}
        pagination
        paginationMode="server"
        sortingMode="server"
        paginationModel={pagination}
        onPaginationModelChange={setPagination}
        sortModel={presets.sortModel}
        onSortModelChange={presets.onSortModelChange}
        columnVisibilityModel={presets.columnVisibilityModel}
        onColumnVisibilityModelChange={presets.onColumnVisibilityModelChange}
        onColumnOrderChange={() => presets.onColumnOrderChange(gridColumnFieldsSelector(apiRef))}
        pageSizeOptions={[25, 50, 100, 200]}
        checkboxSelection
        disableRowSelectionOnClick
        disableColumnFilter
        isRowSelectable={({ row }) => row.replaceable}
        rowSelectionModel={selectionModel}
        onRowSelectionModelChange={handleSelectionChange}
        keepNonExistentRowsSelected
        slots={{
          noRowsOverlay: () => (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                {hasSearch
                  ? 'Nothing contains that text. Remember this searches text only — it does not follow references.'
                  : 'Enter something to search for.'}
              </Typography>
            </Box>
          ),
          toolbar: GridControlsToolbar as GridSlots['toolbar'],
        }}
        slotProps={{
          loadingOverlay: { variant: 'linear-progress', noRowsVariant: 'skeleton' },
          toolbar: {
            // Manual refresh only: results are pinned server-side on purpose, so
            // they don't change under a reviewer — no auto-refresh menu here.
            // Each click is a fresh `refresh=1` recompute (a new nonce, so it
            // can't join an in-flight one) — hence disabled while fetching.
            refresh: gridRefreshToolbar(query, undefined, {
              onRefresh: forceRefresh,
              disabled: !hasSearch || query.isFetching,
              tooltip: hasSearch
                ? 'Re-run the search. Results stay pinned while you page through them and while you review a replacement, so they do not change under you — use this to pick up edits made since.'
                : 'Enter a search first',
            }),
            hideFilterButton: true,
            exportFileName: 'find-replace',
            presets: presets.toolbar,
            batch: {
              actions: batchActions,
              ids: selectedIds,
              allMatching,
              count: selectedCount,
              matched: rowCount,
              // This grid has no generic `/batch/` endpoint — the Replace action
              // supplies its own dialog and dispatch, so the standard path is
              // never taken. Reject loudly if a future action forgets that.
              filterQuery: '',
              runStandard: () =>
                Promise.reject(
                  new Error('Find & Replace has no generic /batch/ endpoint; use the dialog.'),
                ),
              clearSelection,
              onSelectAllMatching: () => setAllMatching(true),
              isPending: false,
            },
          } as GridToolbarProps,
        }}
      />

    </Box>
  );
}
